
import { useState, useEffect, useMemo } from "react";
import useFetch from "./useFetch";
import { useTranslation } from "react-i18next";

const useGetCity = (countryID) => {
  const { t, i18n } = useTranslation();
  
  const [data, setData] = useState({
    cities: [],
  });
  const [dataCountryID, setDataCountryID] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tries, setTries] = useState(0);

  const retry = () => setTries((prev) => prev + 1);

  const Params = new URLSearchParams({
    populate: "*",
    locale: 'en',
    "pagination[limit]": -1,
    "filters[Country][id][$eq]": countryID,
  });
  if (tries > 0) Params.set("_retry", tries);

  const citiesBaseUrl = "/cities";
  const citiesUrl = `${citiesBaseUrl}?${Params.toString()}`;
  
  const {
    data: citiesData,
    loading: citiesLoading,
    error: citiesError,
  } = useFetch(countryID ? citiesUrl : "");

  const processedCities = useMemo(() => {
    if (!citiesData || !Array.isArray(citiesData)) return [];
    return citiesData.map((city) => ({
      ...city,
      value: city.id,
      label: city.attributes?.CityName || city.attributes?.name || "City"
    }));
  }, [citiesData]);

  useEffect(() => {
    if (!countryID) {
      setData({ cities: [] });
      setDataCountryID(null);
      setLoading(false);
      return;
    }

    if (citiesError) {
      // Still attribute the data to the current country, otherwise the
      // staleness check keeps `loading` stuck on true and the city list never
      // settles.
      setData({ cities: [] });
      setDataCountryID(countryID);
      setError(true);
      setLoading(false);
      return;
    }

    if (!citiesLoading) {
      setData({ cities: processedCities });
      setDataCountryID(countryID);
      setLoading(false);
      setError(false);
    } else {
      // A new country is being fetched. Keep reporting loading and drop the
      // previous country's cities so nothing downstream can read stale data.
      setLoading(true);
      setData({ cities: [] });
      setDataCountryID(null);
    }
  }, [citiesLoading, processedCities, citiesError, countryID]);

  // Treat the data as unusable until it belongs to the currently selected
  // country, so a late response can never repopulate the dropdown with the
  // previous country's cities.
  // Truthiness (not `!= null`) so an unset id of `0` or `""` matches the
  // `!countryID` early return above and does not pin `loading` to true forever.
  const isStale = !!countryID && dataCountryID != countryID;

  return {
    data: isStale ? { cities: [] } : data,
    loading: isStale || loading,
    error,
    setError,
    retry,
  };
};

export { useGetCity };